import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  FireIcon,
  StarIcon,
  LogoIcon,
  ArrowLeftIcon,
  ExternalLinkIcon,
} from '../icons/icons';

const About = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const features = [
    {
      icon: <SparklesIcon className="w-6 h-6 text-cyan-400" />,
      title: 'Real-Time Market Terminal',
      description:
        'Streaming live cryptocurrency prices, market caps, 24-hour volume changes, and 7-day sparkline trends across thousands of digital assets.',
      badge: 'Streaming',
    },
    {
      icon: <FireIcon className="w-6 h-6 text-amber-400" />,
      title: 'Trending & Momentum Radar',
      description:
        'Instant detection of the most searched cryptocurrencies, top gainers, and top losers powered by algorithmic volume and query tracking.',
      badge: 'Algorithmic',
    },
    {
      icon: <StarIcon filled={true} className="w-6 h-6 text-amber-400" />,
      title: 'Privacy-First Watchlists',
      description:
        'Pin your favorite tokens to your local portfolio watchlist. Zero account creation required, stored securely in your browser.',
      badge: 'Local Vault',
    },
    {
      icon: <span className="text-xl">💱</span>,
      title: 'Multi-Currency Conversion',
      description:
        'Instant real-time fiat conversion between USD ($), EUR (€), GBP (£), INR (₹), JPY (¥), AUD (A$), and CAD (C$).',
      badge: '7 Currencies',
    },
    {
      icon: <span className="text-xl">📈</span>,
      title: 'Interactive Pro Charts',
      description:
        'Dynamic multi-timeframe historical price charting (24H, 7D, 30D, 90D, 1Y) with smooth bezier curves and area gradients.',
      badge: 'Chart.js Pro',
    },
    {
      icon: <span className="text-xl">🛡️</span>,
      title: 'Resilient API Engine',
      description:
        'Built-in in-memory caching and fail-safe fallback datasets to deliver zero-downtime, continuous market surveillance.',
      badge: 'Zero Downtime',
    },
  ];

  const techStack = [
    { name: 'React 18', role: 'UI Engine & State', color: 'text-cyan-400' },
    { name: 'Tailwind CSS', role: 'Design System & Glassmorphism', color: 'text-sky-400' },
    { name: 'Chart.js', role: 'Interactive Canvas Rendering', color: 'text-rose-400' },
    { name: 'CoinGecko API', role: 'Global Market Data Source', color: 'text-emerald-400' },
    { name: 'Axios', role: 'HTTP Client & Cache Layer', color: 'text-indigo-400' },
    { name: 'React Router', role: 'Client-Side SPA Routing', color: 'text-amber-400' },
  ];

  return (
    <main className="wrapper-container py-8 space-y-12">
      {/* Top Back Breadcrumb */}
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-cyan-400 transition-colors px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>Back to Markets</span>
        </Link>
      </div>

      {/* Hero Showcase Banner */}
      <section className="relative overflow-hidden glass-card p-8 md:p-12 border-slate-800/80 bg-gradient-to-br from-dark-900/90 via-dark-850/80 to-slate-900/90">
        <div className="absolute -top-28 -right-28 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-28 -left-28 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase tracking-wider">
            <LogoIcon className="w-4 h-4" />
            <span>About CryptoPulse Terminal</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-slate-100 tracking-tight leading-tight">
            Democratizing Access to <span className="text-gradient-cyan">Crypto Intelligence</span>
          </h1>

          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
            CryptoPulse is an open-source, ultra-fast cryptocurrency analytics terminal engineered to give traders, developers, and blockchain enthusiasts real-time clarity across thousands of digital assets.
          </p>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="space-y-6">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            Engineered for Precision & Speed
          </h2>
          <p className="text-xs sm:text-sm text-slate-400">
            A comprehensive suite of market surveillance and analytics tools built for modern financial decentralized systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feat, idx) => (
            <div
              key={idx}
              className="glass-card glass-card-hover p-6 flex flex-col justify-between space-y-4 border-slate-800"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    {feat.icon}
                  </div>
                  <span className="text-[11px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 border border-slate-700/50">
                    {feat.badge}
                  </span>
                </div>

                <h3 className="text-base font-bold text-slate-100">{feat.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{feat.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tech Stack & Architecture */}
      <section className="glass-card p-8 border-slate-800 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800/80 pb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-100">Technology Stack & Architecture</h2>
            <p className="text-xs text-slate-400 mt-1">
              Modern frontend technologies optimized for sub-second rendering and low footprint.
            </p>
          </div>
          <span className="text-xs font-mono text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-3 py-1.5 rounded-xl self-start md:self-auto font-semibold">
            React 18 + Tailwind SPA
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {techStack.map((tech, idx) => (
            <div
              key={idx}
              className="bg-slate-900/70 p-4 rounded-xl border border-slate-800/60 text-center space-y-1"
            >
              <div className={`font-extrabold text-sm ${tech.color}`}>{tech.name}</div>
              <div className="text-[11px] text-slate-400">{tech.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Data Source & Attribution */}
      <section className="glass-card p-8 border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-2 max-w-xl text-center md:text-left">
          <h3 className="text-lg font-bold text-slate-100">Public Market Data Attribution</h3>
          <p className="text-xs text-slate-400 leading-relaxed">
            Market prices, volume, rankings, historical chart aggregates, and metadata are sourced through the CoinGecko public API v3.
          </p>
        </div>

        <a
          href="https://www.coingecko.com/en/api"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-500/40 text-emerald-400 text-xs font-bold transition-all shadow-md shrink-0"
        >
          <span>CoinGecko API</span>
          <ExternalLinkIcon className="w-4 h-4" />
        </a>
      </section>
    </main>
  );
};

export default About;
